import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getStyleFilename, getStyleExt } from '@/lib/styles';
import {
  Loader2,
  Upload,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
} from 'lucide-react';

type GenerationFormData = {
  productName: string;
  additionalInstructions?: string;
  style: string;
  aspectRatio: string;
};

type RouterState = {
  storagePath?: string;
  originalImageUrl?: string;
} | null;

// ── Step indicator ──────────────────────────────────────────────────────────

type StepIndicatorProps = {
  currentStep: number;
  steps: string[];
};

function StepIndicator({ currentStep, steps }: StepIndicatorProps) {
  return (
    <div className="flex items-center justify-center px-4 pt-4 pb-2">
      {steps.map((label, index) => {
        const stepNum = index + 1;
        const isCompleted = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        return (
          <div key={stepNum} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                  isCompleted
                    ? 'bg-blue-600 text-white'
                    : isActive
                    ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : stepNum}
              </div>
              <span
                className={cn(
                  'text-xs font-medium whitespace-nowrap',
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : isCompleted
                    ? 'text-blue-500 dark:text-blue-500'
                    : 'text-gray-400 dark:text-gray-500'
                )}
              >
                {label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'h-0.5 w-10 sm:w-16 mx-1 mb-5 transition-all',
                  isCompleted ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Style card with image preview ───────────────────────────────────────────

type StyleCardVisualProps = {
  styleId: string;
  label: string;
  description: string;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: (id: string) => void;
};

function StyleCardVisual({
  styleId,
  label,
  description,
  isSelected,
  disabled = false,
  onSelect,
}: StyleCardVisualProps) {
  const imageUrl = `/style-examples/${getStyleFilename(styleId)}-1.${getStyleExt(styleId)}`;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(styleId)}
      className={cn(
        'relative rounded-xl border-2 overflow-hidden transition-all text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 bg-white dark:bg-gray-800',
        isSelected
          ? 'border-blue-600 ring-2 ring-blue-600 ring-offset-1 shadow-md'
          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Example image */}
      <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-700">
        <img
          src={imageUrl}
          alt={label}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {isSelected && (
          <div className="absolute inset-0 bg-blue-600/10" />
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 bg-blue-600 rounded-full p-1 shadow-md">
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
      </div>
      {/* Label */}
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">{description}</p>
      </div>
    </button>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function NewGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = location.state as RouterState;

  const [currentStep, setCurrentStep] = useState(1);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadedStoragePath, setUploadedStoragePath] = useState<string | null>(
    routerState?.storagePath ?? null
  );
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(
    routerState?.originalImageUrl ?? null
  );
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    if (routerState?.originalImageUrl) {
      setImagePreview(routerState.originalImageUrl);
    }
  }, [routerState?.originalImageUrl]);

  const generationSchema = useMemo(
    () =>
      z.object({
        productName: z
          .string()
          .min(5, t('newGeneration.validation.productNameMin', 'Descreva melhor seu produto (mín. 5 caracteres)'))
          .max(100, t('newGeneration.validation.productNameMax', 'Máximo 100 caracteres')),
        additionalInstructions: z.string().max(500).optional().or(z.literal('')),
        style: z.string().min(1, t('newGeneration.validation.styleRequired', 'Selecione um estilo')),
        aspectRatio: z.string().min(1, t('newGeneration.validation.ratioRequired', 'Selecione um formato')),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<GenerationFormData>({
    resolver: zodResolver(generationSchema),
    defaultValues: {
      productName: '',
      additionalInstructions: '',
      style: '',
      aspectRatio: '',
    },
  });

  const selectedStyle = watch('style');
  const selectedRatio = watch('aspectRatio');
  const productNameValue = watch('productName');
  const hasImage = imageFile !== null || uploadedStoragePath !== null;
  const imageFromRouterState = !imageFile && !!uploadedStoragePath;

  const STYLES = [
    {
      value: 'CONTEXTO_USO',
      label: t('newGeneration.styles.CONTEXTO_USO', 'Contexto de Uso'),
      description: t('newGeneration.stylesDesc.context', 'Produto no seu cenário natural de uso'),
    },
    {
      value: 'EDITORIAL_LUXO',
      label: t('newGeneration.styles.EDITORIAL_LUXO', 'Editorial Luxo'),
      description: t('newGeneration.stylesDesc.luxury', 'Estilo magazine de alto padrão'),
    },
    {
      value: 'LIFESTYLE_MINIMAL',
      label: t('newGeneration.styles.LIFESTYLE_MINIMAL', 'Lifestyle Minimal'),
      description: t('newGeneration.stylesDesc.lifestyle', 'Estética limpa e escandinava'),
    },
    {
      value: 'STUDIO_PROFISSIONAL',
      label: t('newGeneration.styles.STUDIO_PROFISSIONAL', 'Estúdio Profissional'),
      description: t('newGeneration.stylesDesc.studio', 'Iluminação de estúdio comercial'),
    },
  ];

  const ASPECT_RATIOS = [
    {
      value: '1:1',
      label: t('newGeneration.ratios.square', 'Quadrado'),
      description: t('newGeneration.ratios.squareDesc', 'Feed e marketplaces'),
      shape: 'aspect-square',
    },
    {
      value: '4:5',
      label: t('newGeneration.ratios.portrait', 'Retrato'),
      description: t('newGeneration.ratios.portraitDesc', 'Instagram e Shopee'),
      shape: 'aspect-[4/5]',
    },
    {
      value: '16:9',
      label: t('newGeneration.ratios.landscape', 'Paisagem'),
      description: t('newGeneration.ratios.landscapeDesc', 'Facebook e banners'),
      shape: 'aspect-video',
    },
  ];

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setImageFile(file);
      setImageError('');
      setUploadedStoragePath(null);
      setUploadedImageUrl(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
      'image/avif': ['.avif'],
      'image/heic': ['.heic', '.HEIC'],
      'image/heif': ['.heif', '.HEIF'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    disabled: loading,
    onDropRejected: (rejections) => {
      const rejection = rejections[0];
      if (rejection?.errors[0]?.code === 'file-too-large') {
        setImageError(t('newGeneration.errors.fileTooLarge', 'Arquivo muito grande. Máximo 10MB.'));
      } else if (rejection?.errors[0]?.code === 'file-invalid-type') {
        setImageError(
          t('newGeneration.errors.invalidFileType', 'Tipo inválido. Use PNG, JPG, WebP, AVIF ou HEIC.')
        );
      } else {
        setImageError(t('newGeneration.errors.uploadFailed', 'Falha ao carregar arquivo.'));
      }
    },
  });

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!hasImage) {
        setImageError(t('newGeneration.errors.imageRequired', 'Envie uma foto do produto'));
        return;
      }
      const valid = await trigger('productName');
      if (!valid) return;
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 2) {
      const valid = await trigger('style');
      if (!valid) return;
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onSubmit = async (data: GenerationFormData) => {
    if (!hasImage) {
      setImageError(t('newGeneration.errors.imageRequired', 'Envie uma foto do produto'));
      return;
    }

    setImageError('');
    setLoading(true);

    try {
      let finalImageUrl: string;
      let finalStoragePath: string;

      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadResponse = await api.post<{ url: string; storagePath: string }>(
          '/api/upload',
          formData
        );
        finalImageUrl = uploadResponse.url;
        finalStoragePath = uploadResponse.storagePath;
      } else {
        finalImageUrl = uploadedImageUrl!;
        finalStoragePath = uploadedStoragePath!;
      }

      const result = await api.post<{
        id: string;
        imageAnalysis: string;
        prompts: Array<{ title: string; description: string; prompt: string }>;
      }>('/api/generations/prompts', {
        productName: data.productName.trim(),
        originalImageUrl: finalImageUrl,
        originalImageStoragePath: finalStoragePath,
        style: data.style,
        aspectRatio: data.aspectRatio,
        additionalInstructions: data.additionalInstructions?.trim() || undefined,
      });

      navigate(`/generation/${result.id}/prompts`, {
        state: {
          prompts: result.prompts,
          imageAnalysis: result.imageAnalysis,
          originalImageUrl: finalImageUrl,
          originalImageStoragePath: finalStoragePath,
          style: data.style,
          aspectRatio: data.aspectRatio,
          productName: data.productName.trim(),
        },
      });
    } catch (err: unknown) {
      console.error('Generation/Upload failed:', err);
      const message = err instanceof Error ? err.message : String(err);
      setImageError(message || t('newGeneration.errors.generationFailed', 'Falha ao gerar sugestões'));
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  const STEP_LABELS = ['Produto', 'Visual', 'Formato'];

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto">
        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} steps={STEP_LABELS} />

        <form onSubmit={handleSubmit(onSubmit)} className="px-4 pb-32">

          {/* ── Step 1: Produto ─────────────────────────────────────────── */}
          {currentStep === 1 && (
            <div className="space-y-5 mt-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Foto do produto</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Envie uma foto clara com fundo neutro
                </p>
              </div>

              {/* Upload area or "already uploaded" badge */}
              {imageFromRouterState ? (
                <div className="flex items-center gap-3 p-3 border-2 border-green-200 dark:border-green-800 rounded-xl bg-green-50 dark:bg-green-900/20">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">Foto já enviada</p>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mt-2 h-20 w-20 object-cover rounded-lg"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedStoragePath(null);
                      setUploadedImageUrl(null);
                      setImagePreview('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline flex-shrink-0"
                  >
                    Trocar
                  </button>
                </div>
              ) : (
                <>
                  {imagePreview ? (
                    /* Preview with change option */
                    <div className="relative rounded-xl overflow-hidden border-2 border-blue-600 bg-gray-50 dark:bg-gray-800">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full max-h-64 object-contain mx-auto"
                        onError={() => setImageError(t('newGeneration.errors.invalidImage', 'Imagem inválida'))}
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <div className="bg-blue-600 rounded-full p-1 shadow">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div
                        {...getRootProps()}
                        className="absolute inset-0 flex items-end justify-center pb-3 cursor-pointer"
                      >
                        <input {...getInputProps()} />
                        <span className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                          Trocar foto
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* Empty drop zone */
                    <div
                      {...getRootProps()}
                      className={cn(
                        'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                        isDragActive
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : isDragReject || imageError
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 bg-gray-50 dark:bg-gray-800/50',
                        loading && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center gap-3">
                        <div
                          className={cn(
                            'h-14 w-14 rounded-full flex items-center justify-center',
                            isDragActive ? 'bg-blue-200 dark:bg-blue-800' : 'bg-blue-100 dark:bg-blue-900'
                          )}
                        >
                          <Upload
                            className={cn(
                              'h-7 w-7',
                              isDragActive ? 'text-blue-700 dark:text-blue-300' : 'text-blue-600 dark:text-blue-400'
                            )}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {isDragActive
                              ? 'Solte aqui'
                              : 'Toque para escolher uma foto'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            PNG, JPG, WebP até 10MB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {imageError && (
                <p className="text-sm text-red-500 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {imageError}
                </p>
              )}

              {/* Product Name */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">
                  Descreva seu produto <span className="text-red-500">*</span>
                </Label>
                <Input
                  {...register('productName')}
                  placeholder="Ex: Brinco prata formato gota, Tênis Nike Air Max branco..."
                  disabled={loading}
                  className="h-12 text-base"
                />
                <p className="text-xs text-gray-400">
                  Seja específico: inclua tipo, marca, modelo, cor, material. Quanto mais detalhes, melhor o resultado.
                </p>
                {errors.productName && (
                  <p className="text-sm text-red-500 flex items-center gap-1.5">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {errors.productName.message}
                  </p>
                )}
              </div>

              {/* Additional Instructions */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Instruções adicionais{' '}
                  <span className="font-normal text-gray-400">(opcional)</span>
                </Label>
                <textarea
                  {...register('additionalInstructions')}
                  placeholder="Ex: Mostrar o brinco numa orelha feminina, fundo claro, ângulo de 45 graus..."
                  maxLength={500}
                  disabled={loading}
                  className="flex min-h-[90px] w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex justify-end">
                  <span className="text-xs text-gray-400">
                    {(watch('additionalInstructions') || '').length}/500
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Estilo Visual ────────────────────────────────────── */}
          {currentStep === 2 && (
            <div className="space-y-4 mt-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Visual das fotos</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Escolha o estilo que combina com seu produto
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {STYLES.map((style) => (
                  <StyleCardVisual
                    key={style.value}
                    styleId={style.value}
                    label={style.label}
                    description={style.description}
                    isSelected={selectedStyle === style.value}
                    disabled={loading}
                    onSelect={(id) => {
                      setValue('style', id, { shouldValidate: true });
                      setValue('aspectRatio', '');
                    }}
                  />
                ))}
              </div>

              {errors.style && (
                <p className="text-sm text-red-500 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {errors.style.message}
                </p>
              )}
            </div>
          )}

          {/* ── Step 3: Formato ──────────────────────────────────────────── */}
          {currentStep === 3 && (
            <div className="space-y-5 mt-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Formato da imagem</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Escolha a proporção ideal para onde vai usar
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {ASPECT_RATIOS.map((ratio) => (
                  <button
                    key={ratio.value}
                    type="button"
                    onClick={() => setValue('aspectRatio', ratio.value, { shouldValidate: true })}
                    className={cn(
                      'flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                      selectedRatio === ratio.value
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-600 ring-offset-1'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    )}
                  >
                    {/* Visual ratio shape */}
                    <div className="flex items-center justify-center w-full h-12">
                      <div
                        className={cn(
                          'border-2 rounded',
                          selectedRatio === ratio.value
                            ? 'border-blue-600 bg-blue-100 dark:bg-blue-900/40'
                            : 'border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-700',
                          ratio.value === '1:1' && 'w-9 h-9',
                          ratio.value === '4:5' && 'w-8 h-10',
                          ratio.value === '16:9' && 'w-12 h-7'
                        )}
                      />
                    </div>
                    <div className="text-center">
                      <p
                        className={cn(
                          'text-sm font-bold',
                          selectedRatio === ratio.value
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {ratio.value}
                      </p>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 leading-tight">
                        {ratio.label}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-tight">
                        {ratio.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {errors.aspectRatio && (
                <p className="text-sm text-red-500 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {errors.aspectRatio.message}
                </p>
              )}

              {/* Summary card */}
              {selectedStyle && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Resumo
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={`/style-examples/${getStyleFilename(selectedStyle)}-1.${getStyleExt(selectedStyle)}`}
                      alt={selectedStyle}
                      className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {STYLES.find((s) => s.value === selectedStyle)?.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {productNameValue}
                        {selectedRatio && ` · ${selectedRatio}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {imageError && (
                <p className="text-sm text-red-500 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {imageError}
                </p>
              )}
            </div>
          )}
        </form>

        {/* ── Fixed bottom navigation bar ──────────────────────────────── */}
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading}
                data-testid="back-btn"
                className="flex-shrink-0 w-12 h-12 p-0 rounded-xl"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                disabled={loading}
                className="flex-shrink-0 px-4 h-12 rounded-xl"
              >
                Cancelar
              </Button>
            )}

            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={handleNextStep}
                disabled={loading}
                className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-base font-semibold"
              >
                Próximo
                <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={loading || !selectedRatio}
                className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Analisando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Gerar fotos
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
